import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import { detailFieldRecipe } from '../recipes/mobile.recipe';
import { Stack } from './Layout';
import { Text } from './Text';

export interface DetailFieldProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** What the value is: "Customer", "Pickup Location". */
  label: React.ReactNode;
  /** Nothing, or an empty string, reads as a dash. */
  value: React.ReactNode;
  /**
   * One round button at the right, for what you do with the value: call
   * the customer, get directions to the pickup.
   */
  action?: React.ReactNode;
}

/**
 * One fact about the thing on a phone screen, the label over its value:
 * the Figma's call details — Type, Customer, Priority, Pickup Location.
 *
 *   <DetailField label="Customer" value="408 410 2219"
 *     action={<Button variant="secondary" size="icon-lg" pill aria-label="Call">
 *       <PhoneCall /></Button>} />
 *
 * The label muted and the value in ink, both at the large body size; a
 * round wash button at the right when there is something to do with it.
 * Stacked rather than label-left value-right, because a phone's width is
 * better spent on an address than on a column of labels.
 */
export const DetailField = React.forwardRef<HTMLDivElement, DetailFieldProps>(
  function DetailField({ label, value, action, className, ...props }, ref) {
    const empty = value === null || value === undefined || value === '';
    return (
      <div ref={ref} className={cn(detailFieldRecipe(), className)} {...props}>
        <Stack gap={0} className="min-w-0">
          <Text as="span" variant="body-lg-strong" tone="muted">
            {label}
          </Text>
          <Text as="span" variant="body-lg-strong">
            {empty ? '—' : value}
          </Text>
        </Stack>
        {action}
      </div>
    );
  }
);
